// SPEC-453 M1 — 图层树显示序纯函数（bundle-testable，零依赖）。
//
// The layer panel rendered a flat reversed z-list before M1; objects carrying `parent`
// (frame tree, SPEC-425 layout engine) had no readable hierarchy. flattenLayerTree turns
// the flat objects array (z-order, index 0 = bottom-most) into display rows:
//   - 顶层在上（roots in reversed z-order — the old flat order is preserved when no parent exists）,
//   - 父先于子（a parent row is immediately followed by its subtree）,
//   - 兄弟保持 z 序反转,
//   - depth = parent 链长度（SidePanel indents by 8 + depth*14 px）,
//   - 折叠的 frame 整棵子树不出现在显示序里.
// Cycle guard（structure.ts 先例）: a corrupt parent loop NEVER hangs — any object whose
// ancestor chain re-enters itself degrades to a depth-0 root, deterministically.

import type { CanvasObject } from '../types';

export interface LayerRow {
  obj: CanvasObject;
  /** parent 链长度；0 = 顶层。坏 parent 环 → 0（cycle-guard）。 */
  depth: number;
  /** frame 且确有子节点 → 渲染折叠 chevron。 */
  isFrameParent: boolean;
  /** 该 frame 当前处于折叠态（子孙已隐藏）。 */
  collapsed: boolean;
}

/**
 * objects（z 序）→ 显示序行列表。collapsedIds：折叠态 frame id 集合（SidePanel useState 持有，
 * 默认全展开 → 省略或空集）。纯函数、O(n·depth)、环安全。
 */
export function flattenLayerTree(objects: CanvasObject[], collapsedIds?: ReadonlySet<string>): LayerRow[] {
  const byId = new Map<string, CanvasObject>();
  for (const o of objects) byId.set(o.id, o);

  // classify: depth + root flag. root = 无 parent / 悬空 parent / 自环 / 祖先链成环。
  const info = new Map<string, { depth: number; root: boolean }>();
  for (const o of objects) {
    if (info.has(o.id)) continue;
    const chain: CanvasObject[] = [o];
    const seen = new Set<string>([o.id]);
    let cur = o;
    let cyclic = false;
    for (;;) {
      const pid = cur.parent;
      if (pid == null) break;                 // reached the top
      if (pid === cur.id) { cyclic = true; break; } // self-parent
      const p = byId.get(pid);
      if (!p) break;                          // dangling parent → chain ends, cur stays a root
      if (seen.has(p.id)) { cyclic = true; break; }
      seen.add(p.id);
      chain.push(p);
      cur = p;
    }
    if (cyclic) {
      // 坏环：链上所有节点降级为 depth-0 根（永不挂起，显示序仍含全部对象）
      for (const n of chain) info.set(n.id, { depth: 0, root: true });
    } else {
      for (let i = chain.length - 1; i >= 0; i--) {
        info.set(chain[i].id, { depth: chain.length - 1 - i, root: i === chain.length - 1 });
      }
    }
  }

  const childrenOf = new Map<string, CanvasObject[]>();
  const roots: CanvasObject[] = [];
  for (const o of objects) {
    if (info.get(o.id)!.root || o.parent == null || !byId.has(o.parent)) roots.push(o);
    else {
      const arr = childrenOf.get(o.parent) ?? [];
      arr.push(o);
      childrenOf.set(o.parent, arr);
    }
  }
  // display = top-most first: reverse the bottom→top array order at every sibling level
  roots.reverse();
  for (const arr of childrenOf.values()) arr.reverse();

  const out: LayerRow[] = [];
  const emit = (o: CanvasObject, depth: number) => {
    const kids = childrenOf.get(o.id);
    const isFrameParent = o.type === 'frame' && !!kids && kids.length > 0;
    const collapsed = isFrameParent && (collapsedIds?.has(o.id) ?? false);
    out.push({ obj: o, depth, isFrameParent, collapsed });
    if (collapsed || !kids) return; // 折叠的 frame 子孙不进显示序
    for (const c of kids) emit(c, depth + 1);
  };
  for (const r of roots) emit(r, 0);
  return out;
}

/**
 * Display-slot → objects 数组 index 的拖拽落点映射（SPEC-453 M1，commitDrop 抽取为纯函数）。
 * 保持旧不变量：显示序 top-first，slot i ↔ objects index (len-1)-i —— 无嵌套时显示序与旧
 * reversed 平铺完全一致，结果与 M1 前公式逐字节相同；有嵌套时 slot 跟随树显示序（拖拽只调
 * z 序，不改 parent——「拖入 frame 设 parent」不在本 spec 范围）。
 * 无效落点（dragId 缺失/自投/target 不在显示序）→ null。
 */
export function displayDropIndex(
  objects: CanvasObject[],
  rows: LayerRow[],
  dragId: string,
  targetId: string,
  pos: 'before' | 'after'
): number | null {
  if (!dragId || dragId === targetId) return null;
  const dispIds = rows.map(r => r.obj.id).filter(id => id !== dragId);
  let insertAt = dispIds.indexOf(targetId);
  if (insertAt < 0) return null;
  if (pos === 'after') insertAt += 1;
  return (objects.length - 1) - insertAt;
}
